import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { axios } from "~/core/axios";
import { OBJECT_TYPE, REACT_QUERY_KEY } from "~/core/constants/common";
import { routes } from "~/core/constants/routes";
import type { BackendApiResponse } from "~/core/types/backend-api-response.type";

import useUserSession from "~/lib/organizations/hooks/use-user-session";

import { useGlobalCommandbarStore } from "../store";
import type { ICommandbarItem, SearchObjectsResponse } from "../types/common";
import { CommandbarFilterTabType, CommandbarItemType } from "../types/common";

export const useSearchCommandbar = () => {
  const { userSession } = useUserSession();

  const searchQuery = useGlobalCommandbarStore(st => st.searchQuery);
  const activeFilterTab = useGlobalCommandbarStore(st => st.activeFilterTab);
  const setCommandbarSearchItems = useGlobalCommandbarStore(
    st => st.setCommandbarSearchItems,
  );
  const setActiveCommandbarItemId = useGlobalCommandbarStore(
    st => st.setActiveCommandbarItemId,
  );
  const setLoadingCommandbarItems = useGlobalCommandbarStore(
    st => st.setLoadingCommandbarItems,
  );
  const commandbarCommandItems = useGlobalCommandbarStore(
    st => st.commandbarCommandItems,
  );
  const commandbarPageItems = useGlobalCommandbarStore(
    st => st.commandbarPageItems,
  );

  const { data: searchCommandbarData, isLoading: loadingSearchCommandbarData } =
    useQuery({
      queryKey: [
        REACT_QUERY_KEY.COMMANDBAR_SEARCH_RECORDS,
        searchQuery,
        activeFilterTab,
      ],
      queryFn: async ({ queryKey }) => {
        const [_, searchQuery, activeFilter] = queryKey;

        if (activeFilter === CommandbarFilterTabType.COMMAND) return [];

        const searchQueryParams = new URLSearchParams();
        searchQueryParams.set("q", searchQuery);
        const shouldApplyFilterToSearch =
          activeFilter !== CommandbarFilterTabType.ALL &&
          activeFilter !== CommandbarFilterTabType.COMMAND;
        if (shouldApplyFilterToSearch) {
          searchQueryParams.set("object_type", activeFilter.toLowerCase());
        }
        const { data } = await axios.get<
          BackendApiResponse<SearchObjectsResponse[]>
        >(`/search-objects?${searchQueryParams.toString()}`);

        if (!data.data?.length) return [];

        return data.data.map(
          (searchObj): ICommandbarItem => ({
            id: searchObj.object_id,
            label: searchObj.title,
            description: searchObj.description,
            imageUrl: searchObj.image_url,
            ctaLabel: "Open record",
            type:
              searchObj.object_type === OBJECT_TYPE.ACCOUNT
                ? CommandbarItemType.ACCOUNT
                : searchObj.object_type === OBJECT_TYPE.OPPORTUNITY
                  ? CommandbarItemType.OPPORTUNITY
                  : searchObj.object_type === OBJECT_TYPE.CONTACT
                    ? CommandbarItemType.CONTACT
                    : CommandbarItemType.COMMAND,
            href:
              searchObj.object_type === OBJECT_TYPE.ACCOUNT
                ? routes.account.detailsPage(searchObj.object_id)
                : searchObj.object_type === OBJECT_TYPE.OPPORTUNITY
                  ? routes.opportunity.detailsPage(searchObj.object_id)
                  : searchObj.object_type === OBJECT_TYPE.CONTACT
                    ? routes.contact.detailsPage(searchObj.object_id)
                    : undefined,
            objectType:
              searchObj.object_type === OBJECT_TYPE.ACCOUNT
                ? CommandbarFilterTabType.ACCOUNT
                : searchObj.object_type === OBJECT_TYPE.OPPORTUNITY
                  ? CommandbarFilterTabType.OPPORTUNITY
                  : searchObj.object_type === OBJECT_TYPE.CONTACT
                    ? CommandbarFilterTabType.CONTACT
                    : undefined,
          }),
        );
      },
      enabled: !!userSession,
    });

  useEffect(() => {
    setCommandbarSearchItems(searchCommandbarData || []);

    const hasCommandOrPageItems =
      commandbarCommandItems.length > 0 || commandbarPageItems.length > 0;
    if (searchCommandbarData?.length && !hasCommandOrPageItems) {
      setActiveCommandbarItemId(searchCommandbarData[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCommandbarData, searchQuery]);

  useEffect(() => {
    setLoadingCommandbarItems(loadingSearchCommandbarData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSearchCommandbarData]);
};
